import { createEncryptionKey, createVerificationRecord, verifyRecord } from "@fokused-me/core";
import { LanguageType, SessionDuration, usePromise, useSingleton } from "@fokused-me/web-ui";
import { useCallback } from "react";
import { ISessionInfo } from "./Model";
import { readSystemSession, writeSystemSession } from "./systemStorage";
import { setAppEncryptionKey } from "./utils";

/* eslint-disable-next-line */
const endOfDay = require("date-fns/endOfDay");
/* eslint-disable-next-line */
const addDays = require("date-fns/addDays");

const init = () =>
   readSystemSession().then((s) => {
      console.log("readSession");
      // set defaults
      const ss: ISessionInfo = s || {};
      const session: ISessionInfo = {
         language: ss.language || "en",
         languageSelected: ss.languageSelected || false,
         policyAccepted: ss.policyAccepted || false,
         notepads: ss.notepads || 0,
         sessionDuration: ss.sessionDuration || SessionDuration.NON_PERSISTENT,
         sessionExpires: ss.sessionExpires || new Date(),
         verification: ss.verification,
      };

      if (ss.appAccessKey) {
         session.appAccessKey = ss.appAccessKey;
      }

      if (session.sessionExpires < new Date()) {
         // session expired
         session.appAccessKey = undefined;
         writeSystemSession(session);
      } else if (session.appAccessKey) {
         setAppEncryptionKey(Buffer.from(session.appAccessKey, "base64"));
      }

      return session;
   });

/* eslint-disable-next-line react-hooks/rules-of-hooks */
const useSessionSingleton = useSingleton(() => usePromise(init));

export const useSession = () => {
   const { result: session, setResult: setSession } = useSessionSingleton();

   const updateSession = useCallback(
      (s: ISessionInfo) => {
         if (s.sessionDuration === SessionDuration.NON_PERSISTENT) {
            // Don't persist the key
            const sPersist = { ...s, appAccessKey: undefined };
            return writeSystemSession(sPersist).then(() => setSession(s));
         } else {
            return writeSystemSession(s).then(() => setSession(s));
         }
      },
      [setSession]
   );

   const acceptPolicy = useCallback(
      () => updateSession({ ...session!, policyAccepted: true }),
      [session, updateSession]
   );

   const setLanguage = useCallback(
      (language: LanguageType) => updateSession({ ...session!, language, languageSelected: true }),
      [session, updateSession]
   );

   const setNotepadCount = useCallback(
      (notepads: number) => updateSession({ ...session!, notepads }),
      [session, updateSession]
   );

   const startSession = useCallback(
      (password: string, sessionDuration: SessionDuration) => {
         const appAccessKeyBin = createEncryptionKey(password);
         const appAccessKey = appAccessKeyBin.toString("base64");
         let sessionExpires = endOfDay(new Date());
         switch (sessionDuration) {
            case SessionDuration.FIVE_DAYS:
               sessionExpires = addDays(endOfDay(sessionExpires), 5);
               break;
            case SessionDuration.THIRTY_DAYS:
               sessionExpires = addDays(endOfDay(sessionExpires), 30);
               break;
         }

         const verification = createVerificationRecord(appAccessKeyBin).toString("base64");
         setAppEncryptionKey(appAccessKeyBin);

         updateSession({ ...session!, sessionDuration, appAccessKey, sessionExpires, verification });
      },
      [session, updateSession]
   );

   const verifyPassword = useCallback(
      (password: string) => {
         const appAccessKeyBin = createEncryptionKey(password);
         return verifyRecord(Buffer.from(session!.verification!, "base64"), appAccessKeyBin);
      },
      [session]
   );

   const endSession = useCallback(
      () => updateSession({ ...session!, appAccessKey: undefined, sessionExpires: new Date() }),
      [session, updateSession]
   );

   const isSessionActive = useCallback(
      () => session && session.appAccessKey && session.sessionExpires > new Date(),
      [session]
   );

   return {
      session,
      acceptPolicy,
      setLanguage,
      setNotepadCount,
      startSession,
      endSession,
      isSessionActive,
      verifyPassword,
   };
};
